<script setup>
import { computed, nextTick, onMounted, ref } from 'vue'

const props = defineProps({
  salidas: { type: Array, default: () => [] },
  conductores: { type: Array, default: () => [] },
  grupos: { type: Array, default: () => [] },
})

defineEmits(['ver-salida'])

const getGruposNombres = (salida) =>
  (salida?.grupos || []).map((gId) => props.grupos?.find((g) => g.id === gId)?.name || gId)

const esDeCongregacion = (salida) => !salida?.grupos?.length

const DIAS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado']

const fechaTexto = (fecha) => {
  if (!fecha) return ''
  const d = new Date(fecha)
  return `${DIAS[d.getDay()]} ${d.toLocaleDateString('es-AR', { day: 'numeric', month: 'long' })}`
}

const getConductorName = (id) => {
  if (!id) return ''
  return props.conductores?.find((c) => c.id === id)?.name || ''
}

const hoy = new Date()
hoy.setHours(0, 0, 0, 0)

const esPasada = (fecha) => {
  if (!fecha) return false
  const d = new Date(fecha)
  d.setHours(0, 0, 0, 0)
  return d < hoy
}

// Ordena dejando las salidas ya pasadas primero, para que la próxima quede
// como primer elemento visible (scroll 0) apenas se abre la lista.
const salidasOrdenadas = computed(() =>
  props.salidas
    .map((salida, idx) => ({ salida, idx }))
    .sort((a, b) => {
      const pasadaA = esPasada(a.salida.date)
      const pasadaB = esPasada(b.salida.date)
      if (pasadaA !== pasadaB) return pasadaA ? -1 : 1
      return new Date(a.salida.date) - new Date(b.salida.date)
    })
)

const primerOrdIdxNoPasado = computed(() => {
  const ord = salidasOrdenadas.value
  const found = ord.findIndex((item) => !esPasada(item.salida.date))
  return found === -1 ? 0 : found
})

const listaRef = ref(null)

onMounted(async () => {
  await nextTick()
  const items = listaRef.value?.querySelectorAll('.tarjeta-lista')
  items?.[primerOrdIdxNoPasado.value]?.scrollIntoView({ block: 'start' })
})
</script>

<template>
  <div ref="listaRef" class="mayores-contenido">
    <p class="campo__ayuda" style="margin-top:0">Elegí la salida que querés ver.</p>

    <div v-if="!salidas.length" class="mayores-estado">
      <i class="pi pi-calendar mayores-estado__icono" aria-hidden="true" />
      <p>Todavía no hay salidas de predicación cargadas para este enlace.</p>
    </div>

    <div
      v-for="{ salida, idx } in salidasOrdenadas"
      :key="salida.id ?? idx"
      class="tarjeta-lista tarjeta-lista--clickable"
      :class="{ 'tarjeta-lista--pasada': esPasada(salida.date), 'tarjeta-lista--congregacion': esDeCongregacion(salida) }"
      role="button"
      tabindex="0"
      @click="$emit('ver-salida', idx)"
      @keydown.enter="$emit('ver-salida', idx)"
    >
      <span v-if="esDeCongregacion(salida)" class="mayores-badge mayores-badge--congregacion">
        <i class="pi pi-building" aria-hidden="true" /> Toda la congregación
      </span>
      <div v-else class="mayores-badges">
        <span v-for="nombre in getGruposNombres(salida)" :key="nombre" class="mayores-badge mayores-badge--grupo">
          <i class="pi pi-users" aria-hidden="true" /> Grupo {{ nombre }}
        </span>
      </div>
      <p class="tarjeta-lista__titulo">{{ fechaTexto(salida.date) }}</p>
      <p v-if="salida.hour" class="tarjeta-lista__detalle">
        <i class="pi pi-clock" aria-hidden="true" /> {{ salida.hour }} hs
      </p>
      <p v-if="salida.puntoEncuentro?.name || salida.puntoEncuentro?.direccion" class="tarjeta-lista__detalle">
        <i class="pi pi-map-marker" aria-hidden="true" />
         {{ salida.puntoEncuentro.name || salida.puntoEncuentro.direccion }}
      </p>
      <p v-if="getConductorName(salida.conductor)" class="tarjeta-lista__detalle">
        <i class="pi pi-user" aria-hidden="true" /> {{ getConductorName(salida.conductor) }}
      </p>
      <p v-if="salida.territorios?.length" class="tarjeta-lista__detalle">
        <i class="pi pi-th-large" aria-hidden="true" />
        Territorio{{ salida.territorios.length !== 1 ? 's' : '' }}: {{ salida.territorios.join(', ') }}
      </p>

      <button type="button" class="boton-secundario tarjeta-lista__boton-ver" @click.stop="$emit('ver-salida', idx)">
        Ver más <i class="pi pi-chevron-right" aria-hidden="true" />
      </button>
    </div>
  </div>
</template>
